import React from 'react';
import { useMediaQuery } from 'react-responsive';
import '../TopCabsRoutes/TopCabsRoutes.css';
import './RouteCabs.css';
import { AppContextConsumer } from '../../AppContext';
import { getAppEnv } from '../../../config/env';
import queryString from 'query-string';
import { setDateAsDatePlusN, setPickupTime } from '../../utils/timeUtils';
import { CabData } from '../../pages/runtime-typechecker';
import BPG from '../BPG/BPG';
import { CASE_CAB_CARD } from '../../constants/omniture';
import { getCarTypeForDisplay } from '../../utils/stringUtils';
import { isWebView } from '../../utils/commonUtils';
const isSSR = () => typeof window === 'undefined';

const env = getAppEnv();
const { PUBLIC_URL } = env.raw;

type RouteCabsProps = {
  cityName?: string;
  operatorName?: string;
  data: CabData[] | null | undefined;
  seoUrl: string | null | undefined;
  sourceCityName: string;
  destinationCityName: string;
  rydeEnabled?: boolean;
  isomorph?: boolean | null | undefined;
  reviewLinkEnabled?: boolean | null;
};

const RouteCabs = ({
  data,
  seoUrl,
  sourceCityName,
  destinationCityName,
  rydeEnabled = false,
  reviewLinkEnabled = false
}: RouteCabsProps) => {
  const isMobileDevice = useMediaQuery({
    query: '(max-device-width: 768px)'
  });
  const isMobileDeviceWidthForSeatAndBagInfo = useMediaQuery({
    query: '(max-device-width: 420px)'
  });
  const getCabTypeIcon = (cabType: string): string => {
    const iconMap = {
      HATCHBACK: 'hatchbackIcon',
      SEDAN: 'sedanIcon',
      SUV: 'suvIcon'
    };
    return iconMap[cabType];
  };

  const redirectToListing = (
    url: string | undefined | null,
    omniUrlArgs: string
  ) => {
    if (!url) return;
    const query = queryString.parseUrl(url).query;
    const fromCity = query['fromCity'];
    const toCity = query['toCity'];
    const rediectionUrl = isWebView()
      ? `${setDateAsDatePlusN(
          `mmyt://react/?page=cabsListing&deeplink=true&to=${toCity}&from=${fromCity}&tripType=OW&startDate=21-08-2020&${omniUrlArgs}${CASE_CAB_CARD}`
        )}`
      : `${setPickupTime(
          setDateAsDatePlusN(url)
        )}&${omniUrlArgs}${CASE_CAB_CARD}`;
    window.location = Object(rediectionUrl);
  };

  const redirectToReview = (
    reviewUrl: string | undefined | null,
    omniUrlArgs: string
  ) => {
    if (!reviewUrl) return;
    reviewUrl = `${setDateAsDatePlusN(
      reviewUrl
    )}&${omniUrlArgs}${CASE_CAB_CARD}`;
    window.location = Object(reviewUrl);
  };

  const redirect = (
    listingUrl: string | null | undefined,
    reviewLink: string | null | undefined,
    omniUrlArgs: string
  ) => {
    if (reviewLinkEnabled) redirectToReview(reviewLink, omniUrlArgs);
    else redirectToListing(listingUrl, omniUrlArgs);
  };

  return (
    <AppContextConsumer>
      {({ omniUrlArgs, trackTemplateEvent }) => {
        return (
          <div className="cabTypeWrap">
            <table
              style={{ width: '100%', border: '1px solid #cecece' }}
              className="cabTypeDetails cabTypeDetailsR RouteCabsBorder"
            >
              <tbody>
                {data &&
                  data.map((item, index) =>
                    isMobileDevice ? (
                      <>
                        <tr
                          key={index}
                          className={
                            index === 0
                              ? 'cabTypeRow disableBorder'
                              : 'cabTypeRow'
                          }
                        >
                          <a
                            onClick={e => {
                              trackTemplateEvent(
                                `Cab_SEO_Route_${item.type}_Book${
                                  reviewLinkEnabled ? '_ReviewLinkEnabled' : ''
                                }_Cab_Click`
                              );
                              e.preventDefault();

                              redirect(
                                item.redirection_url,
                                item.reviewURL,
                                omniUrlArgs
                              );
                            }}
                            href={item.redirection_url}
                          >
                            <td
                              style={{ border: '1px solid #cecece' }}
                              className="cabTypeCol"
                            >
                              <div className="makeFlex">
                                <span className="carImgWrap">
                                  <span
                                    className={`cabSeoSprite ${getCabTypeIcon(
                                      item.type ? item.type : ''
                                    )}`}
                                  ></span>
                                </span>
                                <div style={{ flexDirection: 'row' }}>
                                  <div>
                                    <p className="cabTypeRoute">
                                      {seoUrl ? (
                                        <a
                                          className="pointerEventNone cabTypeRoute"
                                          href={seoUrl}
                                        >
                                          {item.vehicle_model}
                                        </a>
                                      ) : (
                                        item.vehicle_model
                                      )}
                                    </p>
                                    <p className="cabTimeDis">
                                      {getCarTypeForDisplay(item.type)}
                                    </p>
                                  </div>
                                  <div
                                    style={{
                                      paddingLeft: '0px',
                                      paddingRight: '0px',
                                      paddingBottom: '0px',
                                      paddingTop: '5px'
                                    }}
                                    className={`${
                                      isMobileDeviceWidthForSeatAndBagInfo
                                        ? ''
                                        : 'makeFlex'
                                    } paddingT5 blackText font16`}
                                  >
                                    <span className="seatBagItems">
                                      <span className="seatIocn cabSeoSprite"></span>{' '}
                                      {item.seat_count} Seats
                                    </span>
                                    <span className="seatBagItems">
                                      <span className="bagsIocn cabSeoSprite"></span>{' '}
                                      {item.luggage_count} Bags
                                    </span>
                                    {<span className="seatBagItems">AC</span>}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td
                              style={{ border: '1px solid #cecece' }}
                              className="cabTypeCol"
                            >
                              <p className="cabTypeIncDis">
                                {item.included_kms} kms included
                              </p>
                              <p className="cabTypeFare">
                                Extra fare ₹{item.per_km_charge}/km after{' '}
                                {item.included_kms} kms
                              </p>
                            </td>
                            <td
                              style={{ border: '1px solid #cecece' }}
                              className="cabTypeCol"
                            >
                              {item.strike_off_amount ? (
                                <div className="pushRight appendBottom3">
                                  <p className="strikeOffPrice">
                                    ₹{item.strike_off_amount}
                                  </p>
                                </div>
                              ) : null}
                              <p className="cabTypePrice">
                                ₹{item.total_amount}
                              </p>
                              <p className="cabTypeOnwards">
                                <span className="hiddenDesktop paddingT5">
                                  {item?.tolls_and_taxes_text}
                                </span>
                                <span className="hiddenMobile">
                                  {item?.tolls_and_taxes_text}
                                </span>
                              </p>
                            </td>
                          </a>
                        </tr>
                        <tr>
                          {!isSSR() && item.best_price_guarantee_details ? (
                            <td style={{ border: '1px solid #cecece' }}>
                              <BPG
                                bpgInfo={item.best_price_guarantee_details}
                                isMobileDevice={true}
                                fromCity={sourceCityName}
                                toCity={destinationCityName}
                              />
                            </td>
                          ) : null}
                        </tr>
                      </>
                    ) : (
                      <>
                        <tr
                          key={index}
                          className={
                            index === 0
                              ? 'cabTypeRow disableBorder'
                              : 'cabTypeRow'
                          }
                        >
                          <td
                            style={{ border: '1px solid #cecece' }}
                            className="cabTypeCol"
                          >
                            <div className="makeFlex">
                              {rydeEnabled ? (
                                <img
                                  className="rydeAssuredDesign"
                                  src={`${PUBLIC_URL}/static/public/rydeNewtagV2.png`}
                                ></img>
                              ) : null}
                              <span
                                className={`carImgWrap appendLeft12 appendTop15 vertlCenter ${
                                  rydeEnabled ? 'appendTop15Important' : ''
                                }`}
                              >
                                <span
                                  className={`cabSeoSprite ${getCabTypeIcon(
                                    item.type ? item.type : ''
                                  )}`}
                                ></span>
                              </span>
                              <div
                                style={{ flexDirection: 'row' }}
                                className={`vertlCenter`}
                              >
                                <div style={{ display: 'inline-flex' }}>
                                  <p className="cabTypeRoute appendRight10">
                                    <p>
                                      {seoUrl ? (
                                        <a
                                          className="pointerEventNone cabTypeRoute"
                                          href={seoUrl}
                                        >
                                          {item.vehicle_model}
                                        </a>
                                      ) : (
                                        item.vehicle_model
                                      )}
                                    </p>
                                  </p>
                                  <p
                                    style={{ fontSize: '16px' }}
                                    className="cabTimeDis"
                                  >
                                    {getCarTypeForDisplay(item.type)}
                                  </p>
                                </div>
                                <div
                                  style={{ padding: '0px', fontSize: '14px' }}
                                  className="cabTypeCol seatBagWrap"
                                >
                                  <span
                                    style={{
                                      padding: '0px',
                                      marginRight: '5px'
                                    }}
                                    className="seatBagItems"
                                  >
                                    <img
                                      className="appendRight5"
                                      width="16px"
                                      height="16px"
                                      alt="seat image"
                                      src={`${PUBLIC_URL}/static/public/seat.png`}
                                    ></img>{' '}
                                    {item.seat_count} Seats
                                  </span>
                                  <span
                                    style={{
                                      padding: '0px',
                                      marginRight: '5px'
                                    }}
                                    className="seatBagItems"
                                  >
                                    <img
                                      className="appendRight5"
                                      width="16px"
                                      height="16px"
                                      alt="bag image"
                                      src={`${PUBLIC_URL}/static/public/bag.png`}
                                    ></img>{' '}
                                    {item.luggage_count} Bags
                                  </span>
                                  {
                                    <span
                                      style={{
                                        padding: '0px',
                                        marginRight: '5px'
                                      }}
                                      className="seatBagItems"
                                    >
                                      <img
                                        className="appendRight5"
                                        width="16px"
                                        height="16px"
                                        alt="ac image"
                                        src={`${PUBLIC_URL}/static/public/ac.png`}
                                      ></img>{' '}
                                      AC
                                    </span>
                                  }
                                </div>
                              </div>
                            </div>
                          </td>
                          <td
                            style={{ border: '1px solid #cecece' }}
                            className="cabTypeCol"
                          >
                            <p className="cabTypeIncDis">
                              {item.included_kms} kms included
                            </p>
                            <p className="cabTypeFare">
                              Extra fare ₹{item.per_km_charge}/km after{' '}
                              {item.included_kms} kms
                            </p>
                          </td>
                          <td
                            style={{ border: '1px solid #cecece' }}
                            className="cabTypeCol"
                          >
                            <p className="cabTypePrice">
                              {`₹${item.total_amount}`}
                              {item.strike_off_amount && (
                                <span className="appendLeft5 strikeOffPrice">
                                  ₹{item.strike_off_amount || ''}
                                </span>
                              )}
                            </p>
                            <p className="cabTypeOnwards">
                              {/* <span className="hiddenDesktop paddingT5">
                              {item?.inclusive_message}
                            </span> */}
                              <span className="hiddenMobile">
                                {item?.tolls_and_taxes_text || ''}
                              </span>
                            </p>
                          </td>
                          <td
                            style={{ border: '1px solid #cecece' }}
                            className="cabTypeCol"
                          >
                            <a
                              className="viewCabBtn"
                              href={(() => {
                                if (reviewLinkEnabled && item?.reviewURL)
                                  return item.reviewURL;
                                else return item.redirection_url;
                              })()}
                              onClick={e => {
                                trackTemplateEvent(
                                  `Cab_SEO_Route_${item.type}_Book${
                                    reviewLinkEnabled
                                      ? '_ReviewLinkEnabled'
                                      : ''
                                  }_Cab_Click`
                                );
                                e.preventDefault();
                                redirect(
                                  item.redirection_url,
                                  item.reviewURL,
                                  omniUrlArgs
                                );
                              }}
                            >
                              {' '}
                              BOOK CAB
                            </a>
                          </td>
                        </tr>
                        <tr>
                          {!isSSR() && item.best_price_guarantee_details ? (
                            <td style={{ border: '1px solid #cecece' }}>
                              <BPG
                                bpgInfo={item.best_price_guarantee_details}
                                isMobileDevice={false}
                                fromCity={sourceCityName}
                                toCity={destinationCityName}
                              />
                            </td>
                          ) : null}
                        </tr>
                      </>
                    )
                  )}
              </tbody>
            </table>
          </div>
        );
      }}
    </AppContextConsumer>
  );
};

export default RouteCabs;
